import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeIcon from '@/components/ThemeIcon';
import { Check } from 'lucide-react';

/**
 * Public theme switcher — small circular button in the navbar that opens
 * a dropdown of all available themes. User-selected theme is stored in
 * localStorage (via ThemeContext.setUserTheme) and survives only in that
 * browser — it doesn't mutate the admin's default theme in the DB.
 *
 * Props:
 *   mode: 'desktop' | 'mobile'  — styling tweaks per breakpoint.
 *   tone: 'dark' | 'light'      — borders/hover colors match current navbar.
 *   accentColor: string          — accent color used for active ring / dot.
 */
const ThemeSwitcher = ({ mode = 'desktop', tone = 'light', accentColor = '#0891b2' }) => {
  const { availableThemes, activeTheme, setUserTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Position dropdown under the button
  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right)
      });
    }
  }, [open]);

  if (!availableThemes || availableThemes.length === 0) return null;

  const isDark = tone === 'dark';

  // Circle button styles
  const btnClasses = [
    'relative rounded-full flex items-center justify-center',
    'transition-all duration-200',
    'focus:outline-none focus-visible:ring-2',
    mode === 'mobile' ? 'w-9 h-9' : 'w-9 h-9 sm:w-10 sm:h-10'
  ].join(' ');

  const btnStyle = {
    background: isDark
      ? 'rgba(162, 133, 212, 0.08)'
      : 'rgba(6, 182, 212, 0.06)',
    border: `1.5px solid ${isDark ? 'rgba(162, 133, 212, 0.35)' : 'rgba(6, 182, 212, 0.25)'}`,
    color: isDark ? '#e2e8f0' : '#0f172a',
    boxShadow: open
      ? `0 0 0 2px ${accentColor}33, 0 4px 12px rgba(0,0,0,0.12)`
      : `0 2px 6px rgba(0,0,0,${isDark ? 0.4 : 0.08})`
  };

  const activeIconName = activeTheme?.preview_icon || 'palette';

  const dropdownBg = isDark ? 'rgba(15, 15, 30, 0.98)' : '#ffffff';
  const dropdownBorder = isDark ? 'rgba(162, 133, 212, 0.25)' : 'rgba(229, 231, 235, 1)';
  const dropdownText = isDark ? '#e2e8f0' : '#1e293b';
  const dropdownMuted = isDark ? '#94a3b8' : '#64748b';
  const dropdownHoverBg = isDark ? 'rgba(62, 201, 224, 0.08)' : '#f3f4f6';
  const dropdownActiveBg = isDark ? 'rgba(62, 201, 224, 0.12)' : '#ecfeff';

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        className={btnClasses}
        style={btnStyle}
        aria-label={`Theme: ${activeTheme?.name || 'Default'}. Click to switch.`}
        aria-expanded={open}
        aria-haspopup="menu"
        title={`Theme: ${activeTheme?.name || 'Default'}`}
        data-testid="theme-switcher-btn"
      >
        <ThemeIcon name={activeIconName} size={18} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="fixed rounded-xl shadow-2xl border overflow-hidden animate-fade-in"
          style={{
            top: `${pos.top}px`,
            right: `${pos.right}px`,
            background: dropdownBg,
            borderColor: dropdownBorder,
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            minWidth: '220px',
            maxWidth: '280px',
            zIndex: 10300
          }}
          data-testid="theme-switcher-menu"
        >
          <div
            className="px-4 py-2.5 text-xs uppercase tracking-wider font-semibold border-b"
            style={{ color: dropdownMuted, borderColor: dropdownBorder }}
          >
            Choose theme
          </div>
          <div className="py-1.5 max-h-[60vh] overflow-y-auto">
            {availableThemes.map((t) => {
              const isActive = activeTheme?.id === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    // If user picks the admin default, clear the override so
                    // they follow the admin's future default changes.
                    setUserTheme(t.is_default ? null : t.id);
                    setOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left transition-colors"
                  style={{
                    color: dropdownText,
                    background: isActive ? dropdownActiveBg : 'transparent'
                  }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = dropdownHoverBg; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  data-testid={`theme-switcher-option-${t.id}`}
                >
                  <span
                    className="flex items-center justify-center rounded-full flex-shrink-0"
                    style={{
                      width: 32,
                      height: 32,
                      background: isDark ? 'rgba(162,133,212,0.1)' : 'rgba(6,182,212,0.08)',
                      border: `1.5px solid ${isActive ? accentColor : (isDark ? 'rgba(162,133,212,0.3)' : 'rgba(6,182,212,0.25)')}`,
                      color: isActive ? accentColor : (isDark ? '#cbd5e1' : '#334155')
                    }}
                  >
                    <ThemeIcon name={t.preview_icon || 'palette'} size={16} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold truncate">{t.name}</span>
                    {t.is_default && (
                      <span className="block text-[10px] uppercase tracking-wider" style={{ color: dropdownMuted }}>
                        Site default
                      </span>
                    )}
                  </span>
                  {isActive && (
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ThemeSwitcher;
